<template>
  <div class="invoice-application-check">
    <el-dialog
      :model-value="visible"
      align-center
      :before-close="() => {
        emit('close');
      }"
      width="548"
    >
      <template #header>
        <h4>
          完税开票申请
        </h4>
      </template>

      <el-skeleton v-if="loading" :rows="2" class="mt-3" active height="20px" />

      <div v-else>
        <div class="title">
          <img src="@core/main/assets/images/invoice-application.png" alt="">

          <div v-if="!isNullFreeTaxOrderAmount" class="item">
            <span>{{ `子订单汇总免税金额` }}</span>
            <span class="text-[#000000]">{{ isNull(info.freeTaxOrderAmount) ? '--' : `${info.freeTaxOrderAmount}元` }}</span>
          </div>
          <div v-if="!isNullStandardOrderAmount" class="item">
            <span>{{ `子订单汇总含税金额` }}</span>
            <span class="text-[#000000]">{{ isNull(info.standardOrderAmount) ? '--' : `${info.standardOrderAmount}元` }}</span>
          </div>
          <div class="item">
            <span>{{ `完税开票科目` }}</span>
            <span class="text-[#000000]">{{ isNull(info.invoiceSubNames) ? '--' : info.invoiceSubNames }}</span>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="flex justify-end">
          <el-button @click="emit('close')">
            取消
          </el-button>
          <el-button 
            v-if="!isNullFreeTaxOrderAmount"
            :loading="saveLoading" 
            type="primary"
            @click="handleApplication(ETaxInvoiceApplyStatus.FREE_TAX)"
          >
            免税订单开票申请
          </el-button>
          <el-button 
            v-if="!isNullStandardOrderAmount" 
            :loading="saveLoading"
            type="primary" 
            @click="handleApplication(ETaxInvoiceApplyStatus.STANDARD)"
          >
            含税订单开票申请
          </el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { queryMatchTaxOrderAmount, submitInvoiceApplication } from '@core/main/api';
import { ElMessage } from 'element-plus';
import { IComputeFreeTaxOrderAmount } from '@core/main/types/crowd-source/invoice-pay-taxes';
import { computed, PropType, ref, watch } from 'vue';
import { isNull } from '@core/tools';
import { ETaxInvoiceApplyStatus } from '@core/main/types';

const emit = defineEmits(['close', 'confirm']);

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  companyId: {
    type: Number,
    default: undefined,
  },
  invoiceSubId: {
    type: Number,
    default: undefined,
  },
  selectedIds: {
    type: Set as PropType<Set<number>>,
    default: () => new Set(),
  },
});

const loading = ref(false);
const saveLoading = ref(false);
const info = ref<IComputeFreeTaxOrderAmount>({});

const isNullFreeTaxOrderAmount = computed(() => info.value && info.value?.freeTaxOrderAmount === 0);
const isNullStandardOrderAmount = computed(() => info.value && info.value?.standardOrderAmount === 0);

async function fetchData() {
  loading.value = true;
  try {
    info.value = await queryMatchTaxOrderAmount({
      companyId: props.companyId,
      invoiceSubId: props.invoiceSubId,
      orderDetailIds: Array.from(props.selectedIds),
    }) || {};
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
  loading.value = false;
}

async function handleApplication(invoiceType: ETaxInvoiceApplyStatus) {
  saveLoading.value = true;
  try {
    await submitInvoiceApplication({
      invoiceSubjectId: props?.invoiceSubId,
      companyId: props?.companyId,
      invoiceType,
      orderDetailIds: Array.from(props.selectedIds),
    }, false);

    ElMessage.success('汇总开票申请成功');
    emit('close');
    emit('confirm');
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
  saveLoading.value = false;
}

watch(
  () => props.visible,
  () => {
    if (props.visible) {
      fetchData();
    }
  },
  { immediate: true },
);
</script>

<style scoped lang="scss">
.invoice-application-check {
  :deep(.el-dialog) {
    padding: 0;

    .el-dialog__header {
      padding: 16px 24px 8px;

      h4 {
        color: #344767;
        font-size: 20px;
        font-weight: 600;
      }

      .el-dialog__headerbtn {
        top: 5px;
      }
    }

    .el-dialog__body {
      padding: 24px;

      .title {
        color: #8c8c8c;

        img {
          @apply h-[80px] mb-[23px];
        }
      }

      .item {
        @apply flex items-center gap-x-4 mb-3;

        span:nth-of-type(1) {
          @apply w-[160px] text-right text-[var(--color-text-desc)];
        }

        span:nth-of-type(2) {
          @apply text-[var(--color-text-primary)] w-[421px] truncate;
        }
      }
    }

    .el-dialog__footer {
      padding: 8px 24px 16px;

      .el-button+.el-button {
        margin-left: 8px;
      }
    }

  }

  :deep(.el-form-item__label) {
    @apply w-[130px] mr-3;
  }
}
</style>
